import React, { useState } from 'react';
import { useCart } from '../CartContext';
import { Link } from 'react-router-dom';
import './Checkout.css';

const Checkout = () => {
  const { cart, clearCart, totalCartPrice } = useCart();
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    address: '', city: '', postalCode: '', country: '',
    cardNumber: '', expiryDate: '', cvv: '', nameOnCard: ''
  });
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({ ...prevState, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const subtotal = totalCartPrice;
    const tax = totalCartPrice * 0.15;
    const total = subtotal + tax;
    const orderId = 'ORD-' + Math.random().toString(36).substr(2, 9).toUpperCase();

    try {
      // Send to Formspree with your actual endpoint
      const response = await fetch('https://formspree.io/f/xjkpyjdl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          _subject: `🎉 New Enerstic Order - ${orderId}`,
          _replyto: formData.email,
          order_id: orderId,
          customer_name: `${formData.firstName} ${formData.lastName}`,
          customer_email: formData.email,
          customer_phone: formData.phone,
          customer_address: `${formData.address}, ${formData.city}, ${formData.postalCode}, ${formData.country}`,
          order_items: cart.map(item => 
            `• ${item.name} - R${item.price.toFixed(2)} x ${item.quantity} = R${(item.price * item.quantity).toFixed(2)}`
          ).join('\n'),
          order_total: `R${total.toFixed(2)}`,
          item_count: cart.reduce((sum, item) => sum + item.quantity, 0),
          tax_amount: `R${tax.toFixed(2)}`,
          subtotal: `R${subtotal.toFixed(2)}`,
          timestamp: new Date().toLocaleString()
        })
      });

      if (response.ok) {
        console.log('✅ Order submitted to Formspree');
        setOrderNumber(orderId);
        setOrderPlaced(true);
        clearCart();
      } else {
        throw new Error('Form submission failed');
      }

    } catch (error) {
      console.error('❌ Formspree submission failed:', error);
      // Still complete order even if form fails
      setOrderNumber(orderId);
      setOrderPlaced(true);
      clearCart();
    } finally {
      setLoading(false);
    }
  };

  const subtotal = totalCartPrice;
  const tax = totalCartPrice * 0.15;
  const total = subtotal + tax;

  if (cart.length === 0 && !orderPlaced) {
    return (
      <div className="checkout-page">
        <div className="checkout-header">
          <h1>Checkout</h1>
        </div>
        <div className="empty-checkout">
          <div className="empty-checkout-icon">🛒</div>
          <h2>Your cart is empty</h2>
          <p>Add some items to proceed with checkout</p>
          <Link to="/shop" className="continue-shopping-btn">
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  if (orderPlaced) {
    return (
      <div className="checkout-page">
        <div className="order-success">
          <div className="success-icon">✓</div>
          <h1>Order Placed Successfully!</h1>
          <p className="order-number">Order Number: {orderNumber}</p>
          <p className="success-message">
            Thank you for your purchase! We've received your order #{orderNumber}. 
            We'll send you a confirmation email shortly and your order will be shipped within 2-3 business days.
          </p>
          <div className="success-actions">
            <Link to="/shop" className="continue-shopping-btn">
              Continue Shopping
            </Link>
            <button className="print-receipt-btn" onClick={() => window.print()}>
              Print Receipt
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-header">
        <h1>Checkout</h1>
        <Link to="/cart" className="back-to-cart">
          ← Back to Cart
        </Link>
      </div>

      <div className="checkout-content">
        <div className="checkout-form-section">
          <form onSubmit={handleSubmit} className="checkout-form">
            <div className="form-section">
              <h3>Shipping Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} required />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} required />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Email Address *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} required />
                </div>
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required />
                </div>
              </div>

              <div className="form-group">
                <label>Address *</label>
                <input type="text" name="address" value={formData.address} onChange={handleInputChange} required />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City *</label>
                  <input type="text" name="city" value={formData.city} onChange={handleInputChange} required />
                </div>
                <div className="form-group">
                  <label>Postal Code *</label>
                  <input type="text" name="postalCode" value={formData.postalCode} onChange={handleInputChange} required />
                </div>
              </div>

              <div className="form-group">
                <label>Country *</label>
                <input type="text" name="country" value={formData.country} onChange={handleInputChange} required />
              </div>
            </div>

            <div className="form-section">
              <h3>Payment Information</h3>
              <div className="form-group">
                <label>Card Number *</label>
                <input type="text" name="cardNumber" value={formData.cardNumber} onChange={handleInputChange} placeholder="1234 5678 9012 3456" required />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Expiry Date *</label>
                  <input type="text" name="expiryDate" value={formData.expiryDate} onChange={handleInputChange} placeholder="MM/YY" required />
                </div>
                <div className="form-group">
                  <label>CVV *</label>
                  <input type="text" name="cvv" value={formData.cvv} onChange={handleInputChange} placeholder="123" required />
                </div>
              </div>

              <div className="form-group">
                <label>Name on Card *</label>
                <input type="text" name="nameOnCard" value={formData.nameOnCard} onChange={handleInputChange} required />
              </div>
            </div>

            <button type="submit" className="place-order-btn" disabled={loading}>
              {loading ? 'Processing...' : `Place Order - R${total.toFixed(2)}`}
            </button>
          </form>
        </div>

        <div className="order-summary-section">
          <div className="order-summary-card">
            <h3>Order Summary</h3>
            <div className="order-items">
              {cart.map(item => (
                <div key={item.id} className="order-item">
                  <img src={item.image} alt={item.name} className="order-item-image" />
                  <div className="order-item-details">
                    <h4>{item.name}</h4>
                    <p>Qty: {item.quantity}</p>
                    <p>R{(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="summary-divider"></div>
            <div className="summary-totals">
              <div className="summary-row">
                <span>Subtotal ({cart.reduce((total, item) => total + item.quantity, 0)} items)</span>
                <span>R{subtotal.toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span>Shipping</span>
                <span>Free</span>
              </div>
              <div className="summary-row">
                <span>Tax (15%)</span>
                <span>R{tax.toFixed(2)}</span>
              </div>
              <div className="summary-divider"></div>
              <div className="summary-row total">
                <span>Total</span>
                <span>R{total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;